import { FC, SyntheticEvent, useEffect, useState } from 'react';

import { AccordionDetails, AccordionSummary, Box, Typography, styled, useTheme } from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import { useTranslation } from 'next-i18next';
import { Element } from 'react-scroll';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { IconSize } from '@components/atoms/fontAwesomeIcon/constants';
import ReadMoreButton from '@components/atoms/readMore';
import ResultPanelEmptyData from '@components/store/data-display/resultPanel/resultPanelEmptyData';
import CourseCurriculumContent from '@components/store/product/courseCurriculum/courseCurriculumContent';
import CourseCurriculumSkeleton from '@components/store/product/courseCurriculum/courseCurriculumSkeleton';
import { useCourseCurriculum } from '@hooks/course/useCourseCurriculum';
import { CourseEntity } from '@redux/course/type/courseDetailType';

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
  ({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&::before': {
      display: 'none',
    },
  }),
);

type CourseCurriculumProps = {
  scrollElementName: string;
  courseDetailSeo: CourseEntity;
};

const CourseCurriculum: FC<CourseCurriculumProps> = ({ scrollElementName, courseDetailSeo }) => {
  const { t } = useTranslation('common');
  const theme = useTheme();

  const { idName } = courseDetailSeo;
  const { courseDetailCurriculumData, courseDetailCurriculumDataLoading: isLoading } = useCourseCurriculum({ idName });

  const [expandedPartIds, setExpandedPartIds] = useState<number[]>([]);
  const [isAllExpand, setIsAllExpand] = useState<boolean>(false);

  useEffect(() => {
    if (!courseDetailCurriculumData?.length && isLoading) {
      return;
    }

    setExpandedPartIds(courseDetailCurriculumData.map((curriculum) => curriculum.partId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const handleAllExpand = (isExpand: boolean) => {
    setIsAllExpand(!isExpand);
    setExpandedPartIds(isExpand ? courseDetailCurriculumData.map((curriculum) => curriculum.partId) : []);
  };

  const handleChangeExpand = (partId: number) => (_event: SyntheticEvent, newExpanded: boolean) => {
    let partIds = [...expandedPartIds];
    if (newExpanded) {
      partIds.push(partId);
    } else {
      partIds = partIds.filter((expandedPartId) => expandedPartId !== partId);
    }

    setExpandedPartIds(partIds);
    setIsAllExpand(!partIds.length);
  };

  const courseCurriculum = () => {
    if (isLoading) {
      return <CourseCurriculumSkeleton />;
    }

    if (!isLoading && !courseDetailCurriculumData?.length) {
      return <ResultPanelEmptyData sx={{ height: 264 }} />;
    }

    return courseDetailCurriculumData
      .map((curriculum) => {
        const { partId, partName, contents } = curriculum;

        if (!contents?.length) {
          return null;
        }

        return (
          <Accordion
            key={partId}
            expanded={expandedPartIds.length > 0 ? expandedPartIds.includes(partId) : false}
            onChange={handleChangeExpand(partId)}
            defaultExpanded
            disableGutters
          >
            <AccordionSummary
              sx={{ background: theme.palette.grey[100] }}
              expandIcon={
                <FontAwesomeIcon
                  icon="fa-regular fa-angle-down"
                  color={theme.palette.primary.main}
                  size={IconSize.sm}
                />
              }
            >
              <Typography variant="h5">{partName}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {contents.map((content) => {
                return <CourseCurriculumContent key={content!.getId()} strategy={content!} courseIdName={idName} />;
              })}
            </AccordionDetails>
          </Accordion>
        );
      })
      .filter(Boolean);
  };

  return (
    <Element name={scrollElementName}>
      <Box display="flex" justifyContent="space-between" alignItems="center" pb={2}>
        <Typography variant="h4">{t('store_course:course_detail.course_curriculum.title')}</Typography>
        <ReadMoreButton
          title_expand={
            <Typography color="primary.main" variant="button_deprecated" fontWeight="500">
              {t('button.hide')}
            </Typography>
          }
          title_unexpand={
            <Typography color="primary.main" variant="button_deprecated">
              {t('button.show')}
            </Typography>
          }
          expand={isAllExpand}
          onClick={() => handleAllExpand(isAllExpand)}
          icon={false}
        />
      </Box>

      <Box>{courseCurriculum()}</Box>
    </Element>
  );
};

export default CourseCurriculum;
